import { BaseService } from './BaseService';
import {
  MedicalSupplyViewModel,
  FarmMedicalSupplyViewModel,
  CreateMedicalSupplyOrderCommand,
  MedicalSupplyOrdersQueryResult,
  MedicalSupplyOrderViewModel,
  FarmDiseasesQueryResult,
  CreateDiseaseCommand,
  MedicalSupplyBottlesQueryResult,
  ConfirmMedicalSupplyOrderCommand,
  DiseaseViewModel
} from '../models/injection';
import { PageableResult } from '../models/common';
import { AnimalType } from '../models/animal';
import { CreateMedicalSupplyBottleCommand } from '../models/vets';

export class MedicalSuppliesService extends BaseService {
  getMedicalSupplies = async (
    page: number,
    size: number,
    number?: string,
    supplyType?: number,
    sort?: string
  ): Promise<PageableResult<FarmMedicalSupplyViewModel>> => {
    const query = this.BuildQueryString({ page, size, number, supplyType, sort });
    const result = await this.Fetch(`/medical-supplies?${query}`, 'GET');
    return this.FromResult<PageableResult<FarmMedicalSupplyViewModel>>(result);
  };

  getAllMedicalSupplies = async (): Promise<FarmMedicalSupplyViewModel[]> => {
    const result = await this.Fetch(`/medical-supplies/all`, 'GET');
    return this.FromResult<FarmMedicalSupplyViewModel[]>(result);
  };

  getFarmMedicalSupplies = async (): Promise<FarmMedicalSupplyViewModel[]> => {
    const result = await this.Fetch('/farm/medical-supplies', 'GET');
    return this.FromResult<FarmMedicalSupplyViewModel[]>(result);
  };

  createMedicalSupply = async (supply: MedicalSupplyViewModel): Promise<string> => {
    const result = await this.Fetch('/medical-supplies', 'POST', JSON.stringify(supply));
    return this.FromResult<string>(result);
  };

  createMedicalSupplyOrder = async (order: CreateMedicalSupplyOrderCommand): Promise<string> => {
    const result = await this.Fetch('/medical-supplies/orders', 'POST', JSON.stringify(order));
    return this.FromResult<string>(result);
  };

  completeMedicalSupplyOrder = async (order: MedicalSupplyOrderViewModel): Promise<string> => {
    const command = JSON.stringify({ orderId: order.id, items: order.items });
    const result = await this.Fetch('/medical-supplies/orders/complete', 'POST', command);
    return this.FromResult<string>(result);
  };

  confirmMedicalSupplyOrder = async (command: ConfirmMedicalSupplyOrderCommand): Promise<string> => {
    const commandJson = JSON.stringify(command);
    const result = await this.Fetch('/medical-supplies/orders/confirm', 'POST', commandJson);
    return this.FromResult<string>(result);
  };

  getMedicalSupplyOrders = async (page: number, size: number): Promise<MedicalSupplyOrdersQueryResult> => {
    const query = this.BuildQueryString({ page, size });
    const result = await this.Fetch(`/medical-supplies/orders?${query}`, 'GET');
    return this.FromResult<MedicalSupplyOrdersQueryResult>(result);
  };

  getMedicalSupplyOrderById = async (id: string): Promise<MedicalSupplyOrderViewModel> => {
    const result = await this.Fetch(`/medical-supplies/orders/${id}`, 'GET');
    return this.FromResult<MedicalSupplyOrderViewModel>(result);
  };

  getMedicalSupplyBottles = async (
    page: number,
    size: number,
    medicalSupplyNumber: string,
    lotNumber: string,
    animalType: AnimalType,
    showHidden: boolean = false,
    sort?: string
  ): Promise<MedicalSupplyBottlesQueryResult> => {
    const query = this.BuildQueryString({ page, size, medicalSupplyNumber, lotNumber, animalType, showHidden, sort });
    const result = await this.Fetch(`/medical-supplies/bottles?${query}`, 'GET');
    return this.FromResult<MedicalSupplyBottlesQueryResult>(result);
  };

  createMedicalSupplyBottle = async (command: CreateMedicalSupplyBottleCommand): Promise<string> => {
    const result = await this.Fetch(`/medical-supplies/bottles`, 'POST', JSON.stringify(command));
    return this.FromResult<string>(result);
  };

  getFarmDiseases = async (page: number, size: number): Promise<FarmDiseasesQueryResult> => {
    const query = this.BuildQueryString({ page, size });
    const result = await this.Fetch(`/diseases?${query}`, 'GET');
    return this.FromResult<FarmDiseasesQueryResult>(result);
  };

  getFarmAllDiseases = async (): Promise<DiseaseViewModel[]> => {
    const result = await this.Fetch(`/diseases/all`, 'GET');
    return this.FromResult<DiseaseViewModel[]>(result);
  };

  createFarmDisease = async (command: CreateDiseaseCommand): Promise<string> => {
    const result = await this.Fetch('/diseases', 'POST', JSON.stringify(command));
    return this.FromResult<string>(result);
  };

  deleteFarmDisease = async (id: string): Promise<string> => {
    const result = await this.Fetch('/diseases', 'DELETE', JSON.stringify({ id }));
    return this.FromResult<string>(result);
  };

  setBottleIsHiddenValue = async (bottleId: string, isHidden: boolean): Promise<boolean> => {
    const command = JSON.stringify({ id: bottleId, isHidden });
    const result = await this.Fetch('/farm/medical-supplies/bottles/visibility', 'PUT', command);
    return this.FromResult<boolean>(result);
  };

  private throwIfNotOk = async (res: Response): Promise<void> => {
    if (res.ok) return;
    const body = await res.json().catch(() => ({}));
    if (body.message) throw new Error(body.message);
    if (body.errors) throw body.errors;
    throw new Error(`Request failed with status ${res.status}`);
  };

  updateMedicalSupplyBottle = async (
    id: string,
    data: {
      animalType: number;
      ccPerDose?: number;
      daysToUse: number;
      dosePerAnimal?: number;
      incrementStep: number;
      kgPerOneCC?: number;
      lotNumber: string;
      maxVolumeCC: number;
      minVolumeCC: number;
      size: number;
      startVolume: number;
      treatmentNumber?: string;
    }
  ): Promise<void> => {
    const res = await this.Fetch(`/medical-supplies/bottles/${id}`, 'PUT', JSON.stringify(data));
    await this.throwIfNotOk(res);
  };

  removeMedicalSupplyBottle = async (id: string): Promise<void> => {
    const res = await this.Fetch(`/medical-supplies/bottles/${id}`, 'DELETE');
    await this.throwIfNotOk(res);
  };

  updateMedicalSupply = async (
    id: string,
    data: { method: number; producer: string; registrationNumber?: string; supplyType: number; waitingTime: number }
  ): Promise<void> => {
    const res = await this.Fetch(`/medical-supplies/${id}`, 'PUT', JSON.stringify(data));
    await this.throwIfNotOk(res);
  };

  deleteMedicalSupply = async (id: string): Promise<void> => {
    const query = this.BuildQueryString({ Id: id });
    const res = await this.Fetch(`/medical-supplies?${query}`, 'DELETE');
    await this.throwIfNotOk(res);
  };
}

export default MedicalSuppliesService;
